import { clientData, ITableData } from '../types/interfaces';
import { PREVIOUS_BALANCE_DOCUMENT_LABEL } from './originationRequiredDocuments';
import { getRuntimeEnv } from './runtimeEnv';
import { UY_COMITE_LEVEL_ROLES } from './uruguayRoles';

export const ADMIN_ROLE = 'ROLE_admin';
export const OWNER_ROLE = 'ROLE_administrator';
export const QUOTE_MARK = '"';
export const COLUMN_SEPARATOR = '-';
export const SPACE_KEY = ' ';
export const MB = 1048576;
export const MAX_FILE_SIZE = 20 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.xls',
  '.xlsx',
  '.doc',
  '.docx',
];
export const ACCEPTED_FORMATS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
export const MSG_EXTENSION = '.msg';
export const MSG_MIME_TYPE = 'application/vnd.ms-outlook';

export const getAcceptedExtensions = (
  isUruguayEnabled = false
): string[] =>
  isUruguayEnabled
    ? [...ACCEPTED_EXTENSIONS, MSG_EXTENSION]
    : ACCEPTED_EXTENSIONS;

export const getAcceptedFormats = (isUruguayEnabled = false): string[] =>
  isUruguayEnabled
    ? [...ACCEPTED_FORMATS, MSG_MIME_TYPE]
    : ACCEPTED_FORMATS;

export const getAcceptedFormatsLabel = (
  isUruguayEnabled = false
): string => getAcceptedExtensions(isUruguayEnabled).join(', ');

export const ACCEPTED_FORMATS_LABEL = getAcceptedFormatsLabel(false);
export const DASHBOARD_PAGE_SIZE = 10;
export const MIME_TYPES_MAP: Record<string, string> = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  msg: 'application/vnd.ms-outlook',
  pdf: 'application/pdf',
  png: 'image/png',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export const APPLICATION_STATUS = {
  revision: {
    code: 1,
    label: 'REV',
    text: 'En revision',
  },
  approved: {
    code: 2,
    label: 'APP',
    text: 'ACEPTADA',
  },
  rejected: {
    code: 3,
    label: 'REJ',
    text: 'DENEGADA',
  },
  pending: {
    code: 4,
    label: 'PEN',
    text: 'Pendiente',
  },
  committee: {
    code: 5,
    label: 'COM',
    text: 'En comité',
  },
  instrumentation: {
    code: 6,
    label: 'INS',
    text: 'En instrumentación',
  },
  readyToInstrument: {
    code: 8,
    label: 'RTI',
    text: 'Para instrumentar',
  },
  finished: {
    code: 7,
    label: 'FIN',
    text: 'CERRADA',
  },
};

export const RISK_STATUS = {
  revision: {
    code: 1,
    label: 'REV',
    text: 'Revisar',
  },
  approved: {
    code: 2,
    label: 'OK',
    text: 'Seguro',
  },
  high_risk: {
    code: 3,
    label: 'HR',
    text: 'Riesgo alto',
  },
  medium_risk: {
    code: 4,
    label: 'MR',
    text: 'Riesgo medio',
  },
};

export const ROLES = {
  producer: 'Productor',
  commercial: 'Comercial',
  admin: 'Administrador',
  riskAnalyst: 'Analista de Riesgo',
  comite: 'Comité',
  instrumentator: 'Instrumentador',
  jcyc: 'Jefe de Créditos y Cobranzas',
  jah: 'Jefe Admin. Horm.',
  jcc: 'Jefe Com. Cem.',
  jch: 'Jefe Com. Horm.',
  ggdhe: 'Gerente General DHE',
  gf: 'Gerente Financiero',
  gm: 'Gerencia Metropolitana',
  gi: 'Gerencia Interior',
  dn: 'Director de Negocios',
  dnh: 'Director de Negocios Hormigón',
  daf: 'Director Administración Financiera',
  gg: 'Gerencia General',
  cd: 'Comité Directivo',
  uyJefeCc: 'Jefe de Créditos y Cobranzas',
  uyhoJefeAd: 'Jefe Administración',
  uyComiteGcia: 'Gerente Finanzas',
  uyComiteGciaCom: 'Gerencia Comercial',
  uyhoComiteCl: 'Director Negocio Hormigones',
  uyComiteCa: 'Director Administración, Finanzas & IT',
  uyDirectorGg: 'Director General',
  uyceComiteCl: 'Director Negocio Cementos',
};

export const ROLES_CAP = {
  producer: 'PRODUCTOR',
  commercial: 'COMERCIAL',
  admin: 'ADMIN',
  riskAnalyst: 'ANALISTA_RIESGO',
  comite: 'COMITE',
  instrumentator: 'INSTRUMENTADOR',
  jcyc: 'JCYC',
  jah: 'JAH',
  jcc: 'JCC',
  jch: 'JCH',
  ggdhe: 'GGDHE',
  gf: 'GF',
  gm: 'GM',
  gi: 'GI',
  dn: 'DN',
  dnh: 'DNH',
  daf: 'DAF',
  gg: 'GG',
  cd: 'CD',
  uyJefeCc: 'UY_JEFE_CC',
  uyhoJefeAd: 'UYHO_JEFE_AD',
  uyComiteGcia: 'UY_COMITE_GCIA',
  uyComiteGciaCom: 'UY_COMITE_GCIA_COM',
  uyhoComiteCl: 'UYHO_COMITE_CL',
  uyComiteCa: 'UY_COMITE_CA',
  uyDirectorGg: 'UY_DIRECTOR_GG',
  uyceComiteCl: 'UYCE_COMITE_CL',
};

// Roles con permisos de nivel Comité
export const COMITE_LEVEL_ROLES = [
  ROLES_CAP.comite,
  ROLES_CAP.jcyc,
  ROLES_CAP.jah,
  ROLES_CAP.jcc,
  ROLES_CAP.jch,
  ROLES_CAP.ggdhe,
  ROLES_CAP.gf,
  ROLES_CAP.gm,
  ROLES_CAP.gi,
  ROLES_CAP.dn,
  ROLES_CAP.dnh,
  ROLES_CAP.daf,
  ROLES_CAP.gg,
  ROLES_CAP.cd,
  ...UY_COMITE_LEVEL_ROLES,
];

export const CURRENCY_CODES = {
  ars: 'ARS',
  usd: 'USD',
  uyu: 'UYU',
};

export const RISK_CODES = {
  nosis: 'NOSIS',
  bpb: 'BPB',
  casfog: 'CASFOG',
  bcra: 'BCRA',
  lideco: 'LIDECO',
};

export const COLUMNS_TABLE_WIDTH = 168;
export const USERS_COLUMN_TABLE_WIDTH = 259;

export const initialClientData: clientData = {
  id: '',
  cuit: '',
  last_comment: '',
  basic_data: {
    company_name: '',
    company_address: '',
    state: '',
    city: '',
    phone_number: '',
    email: '',
    legal_entity: '',
    activity: '',
    iva_status: '',
    date_incorporation: '',
    economic_group: '',
    legal_company_address: '',
    legal_city: '',
    legal_state: '',
    postal_code: null,
    legal_postal_code: null,
    contractual_link: null,
    politically_exposed: null,
    state_supplier_registry: null,
    dgi_status: null,
    dgi_issue_date: null,
    dgi_expiration_date: null,
    bps_certificate_number: null,
    bps_issue_date: null,
    bps_expiration_date: null,
  },
  corporate_structure: [],
  files: [],
  products: [],
  requested_product_lines: [],
  manual_total_amount: true,
  requested_amount: [
    {
      currency_code: 'ARS',
      value: 0,
    },
    {
      currency_code: 'USD',
      value: 0,
    },
  ],
  verified: false,
  reason: '',
  review: '',
  extra_comments: '',
  has_valid_corporate_composition: false,
  signers: [],
  approved_products: [],
  officer: '',
  application_files: [],
  note_amount: null,
  guarantee_amount: null,
  effective_date: undefined,
  credit_type_id: undefined,
  credit_type_name: undefined,
  operation_type_id: undefined,
  operation_type_label: undefined,
};

export function getInitialClientData(): clientData {
  const isCementos = getRuntimeEnv('REACT_APP_CLIENT_NAME') === 'cementos';
  return {
    ...initialClientData,
    manual_total_amount: isCementos ? false : initialClientData.manual_total_amount,
  };
}

export const initialTableData: ITableData = {
  size: 0,
  page: 0,
  totalElements: 0,
  totalPages: 0,
};

export const DOCUMENT_IDS: Record<string, string> = {
  '001':
    'Breve reseña de la empresa (actividad, principales clientes, principales proveedores y demás información útil)',
  '002': 'Declaración de Ganancias',
  '003': 'Declaración de Bienes Personales',
  '004': 'Ventas Mensuales del Último Año',
  '005': 'Deudas Actualizadas',
  '006': 'Certificado Pyme',
  '007': 'Manifestación de Bienes firmado por contador',
  '008':
    'Detalle de la composición accionaria (indicando CUIT de los socios y %)',
  '009': 'Último Estado Contable certificado',
  '103': PREVIOUS_BALANCE_DOCUMENT_LABEL,
  '010': 'Ventas Post Balance',
  '011': 'Estatuto de la empresa',
  '012': 'Actas',
  '013': 'DNI frente',
  '014': 'DNI dorso',
  '015': 'Otros',
  '016': 'Contragarantías',
  '017': 'Balance',
  '018': 'Poderes',
  '019': 'Otros',
};

export const SIGNERS_TYPES = [
  {
    id: '001',
    label: 'Fiador',
  },
  {
    id: '002',
    label: 'Representante legal',
  },
  {
    id: '003',
    label: 'Pagaré',
  },
];

export const CONTRACTS_TYPES = [
  {
    id: '001',
    label: 'Fianza',
  },
  {
    id: '002',
    label: 'Garantia',
  },
  {
    id: '003',
    label: 'Pagare',
  },
];

export const CONTRACTS_STATUSES = {
  pending: {
    code: 1,
    label: 'PEN',
  },
  rejected: {
    code: 2,
    label: 'REJ',
  },
  signed: {
    code: 3,
    label: 'SIG',
  },
};

export const BULK_UPLOAD_ERROR_MESSAGES: Record<string, string> = {
  UPLOAD_FAILED: 'Uno o más archivos no pudieron cargarse. Reintentá o revisá el formato.',
  SELECT_LABEL: 'Asegurate de seleccionar una etiqueta para cada archivo.',
  CONNECTION_ERROR: 'No se pudo completar la carga. Verificá tu conexión o intentá más tarde.',
  DUPLICATE_FILE: 'No se permiten archivos duplicados.',
  FILE_SIZE_EXCEEDED: 'El tamaño de archivo excede el límite permitido.',
  FILE_FORMAT_INVALID: `El formato de archivo solo puede ser: ${ACCEPTED_FORMATS_LABEL}`,
  CUIT_REQUIRED: 'CUIT no proporcionado',
  DOCUMENT_TYPE_NOT_FOUND: 'Tipo de documento no encontrado',
  FILE_UPLOAD_ERROR: 'Error al subir el archivo',
};